import axios from 'axios';
import { Button } from 'antd';
import { CLIENT_ID, DEVELOPER_KEY } from '../../../constants/google';
import config from '../../../config';
import loadScript from 'load-script';
import { log } from '../../log';
import { lookup } from 'mime-types';
import { useEffect, useRef, useState } from 'react';
import { AsyncSVG } from '../AsyncSVG';
import { betaKeys, isBetaEnabled } from '../../beta';
import { GetBrowser } from '../../helpers/utilities';

const GOOGLE_SDK_URL = 'https://apis.google.com/js/api.js';

export default function GooglePicker({
    buttonBackground,
    children,
    multiselect,
    onError,
    onFilesPicked,
    types,
    disabled
}) {
    const [isGoogleScriptLoaded, setIsGoogleScriptLoaded] = useState(false);

    /**
     *  Try to load google script and when is ready I trigger the isGoogleScriptLoaded change to true
     */
    const loadGoogleScript = async () => {
        if (isGoogleScriptLoaded) {
            return Promise.resolve(true);
        }

        if (["firefox"].includes(GetBrowser())) {
            let gApiScript = await fetch(GOOGLE_SDK_URL);
            Function(await gApiScript.text())();
        } else if (["safari"].includes(GetBrowser())) {
            await new Promise(resolve => {
                let scp = document.createElement("script");
                scp.src = GOOGLE_SDK_URL;
                scp.onload = resolve;
                document.body.appendChild(scp);
            });
        } else {
            await new Promise(resolve => {
                loadScript(GOOGLE_SDK_URL, {}, () => {
                    resolve(true);
                });
            });
        }
        setIsGoogleScriptLoaded(true);
        return new Promise(resolve => {
            window.gapi.load('auth', () => {
                window.gapi.load('picker', () => {
                    resolve(true);
                });
            });
        });
    }

    /**
     * Verify the auth and then if everything it's ok try to open up the popup
     */
    const openPopup = () => {
        const jsToken = window.gapi.auth.getToken();
        const oauthToken = jsToken && jsToken.access_token;
        if (oauthToken) {
            openPickupPopup(oauthToken);
            return;
        }
        
        return new Promise((res, rej) => {
            window.gapi.auth.authorize({
                client_id: CLIENT_ID,
                scope: ['https://www.googleapis.com/auth/drive.readonly'],
                immediate: false
            }, (response) => {
                if (response.access_token) {
                    openPickupPopup(response.access_token);
                    res();
                    return;
                }
                console.error('Google authorization fail.', response);
                rej();
            })
        });
    }

    const openPickupPopup = async (token) => {
        const googleViewId = window.google.picker.ViewId['DOCS'];
        const view = new window.google.picker.View(googleViewId);

        view.setMimeTypes(types.split(',').map((value) => {
            return lookup(value);
        }).filter((value) => {
            if (value) return value;
        }).join(','));

        if (!view) {
            throw new Error('Can\'t find view by viewId');
        }
        const onPickerChangeWithToken = onPickerChange.bind(undefined, token);
        const picker = new window.google.picker.PickerBuilder().addView(view).setOAuthToken(token).setDeveloperKey(DEVELOPER_KEY).setCallback(onPickerChangeWithToken);

        picker.enableFeature(window.google.picker.Feature.NAV_HIDDEN);

        if (multiselect) {
            picker.enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED);
        }

        picker.build().setVisible(true);
    }

    const onPickerChange = (token, data) => {
        if (data && data.action === "picked") {
            var fileToUpload = [];
            for (let i = 0; i < data.docs.length; ++i) {
                fileToUpload.push({
                    uri: `https://www.googleapis.com/drive/v3/files/${data.docs[i].id}?alt=media`,
                    fileName: data.docs[i].name,
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
            }
            onFilesPicked(fileToUpload);
        }
    }

    /**
     * If it's not disabled and It's not ssrMode:
     *  - Check if the script is loaded
     *      - Case true I open up the gooogle pop-up
     *      - Case false I load the script function
     *
     * @returns void
     */
    const onChoose = async () => {
        if (config.ssrMode !== 1 && !disabled) {
            if (!isGoogleScriptLoaded) {

                //trick for safari that pass tough popup blocker
                //we have to create a window instance before any async instruction to avoid safari believe the window.open is NOT triggered programatically
                let popupRef = null;
                if (GetBrowser() === "safari") {
                    popupRef = window.open("", "_blank", "popup=yes, width=400,height=500,menubar=no,titlebar=0,toolbar=0");
                }
                
                await loadGoogleScript()     

                if (GetBrowser() === 'safari' && popupRef) {
                    let defaultFn = window.open;
                    window.open = (url) => { popupRef.location = url; return popupRef };
                    openPopup(popupRef).catch(() => {/* Prevent console.error to be triggered */}).finally(() => {
                        window.open = defaultFn;
                    });
                    return;
                }
            }
            openPopup();
        }
    }

    return (
        <Button aria-label="Google Drive" className={`action-button ${buttonBackground}`} onClick={onChoose}>
            <span>
                <AsyncSVG className="cloud-upload-icon" src="/images/upload-google-drive.svg" />
                {children}
            </span>
        </Button>
    );
}
